import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Heading,
  Link,
  OTPInput,
  Text,
} from '@razorpay/blade/components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import ApiService from '../../services/ApiService';
import ReCAPTCHA from 'react-google-recaptcha';
import { setMerchants, setSelectedMerchant } from '@store/slices/merchantSlice';
import { getMerchantsWithLoginFallback } from '@src/utils/merchantHelpers';
import { getErrorMessage, getErrorStatus } from '@src/utils/errorMessage';
import { setCardDetails } from '@store/slices/authDetails';
import { toast } from 'react-toastify';
import type { Merchant } from '@src/types/merchant';

interface OtpInputPageProps {
  username: string;
  password: string;
  onBackToLogin: () => void;
}

const OtpInputPage: React.FC<OtpInputPageProps> = ({
  username,
  password,
  onBackToLogin,
}) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputError, setInputError] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [recaptchaVal, setRecaptchaVal] = useState<null | string>(null);
  const navigate = useNavigate();
  const { handleLogin, handleLogout } = useAuth();
  const [, setCookie] = useCookies([
    'authToken',
    'refreshToken',
    'loungeId',
    'loungeDetails',
    'role',
  ]);
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const isProduction = import.meta.env.VITE_APP_ENV === 'production';

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (countdown > 0 && isResendDisabled) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown, isResendDisabled]);

  const handleResendOtp = async () => {
    try {
      setResendLoading(true);
      await ApiService.post('v1/user/staff/login', {
        username: username,
        password: password,
      });
      setIsResendDisabled(true);
      setCountdown(60);
      setError('');
      setInputError(false);
      setOtp('');
    } catch (error: unknown) {
      setError(getErrorMessage(error, 'Failed to resend OTP'));
    } finally {
      setResendLoading(false);
    }
  };

  const isOtpComplete = otp.length === 6;

  const handleFormSubmit = async () => {
    try {
      setVerifyLoading(true);
      const response = await ApiService.put('v1/user/staff/login', {
        username: username,
        otp: otp,
        ...(isProduction && { captchaToken: recaptchaVal }),
      });
      const payload = response.data;
      if (!payload?.access_token) {
        setError('Invalid verification response. Please try again.');
        return;
      }

      const { access_token, refresh_token, member_details } = payload;

      dispatch(setCardDetails(member_details));

      setCookie('loungeId', member_details?.lounge_id, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('loungeDetails', JSON.stringify(member_details?.lounge), {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('authToken', access_token, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('refreshToken', refresh_token, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('role', member_details?.role, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });

      // Use merchants from verify response first; fallback to merchant API
      const { merchants, error } = await getMerchantsWithLoginFallback(
        member_details?.merchants as Merchant[] | undefined,
      );

      if (!merchants.length) {
        if (error) {
          const status = getErrorStatus(error);
          if (status === 403) {
            toast.error(
              'Access Denied: You do not have permission to access any merchant. Please contact your administrator.',
              { autoClose: 5000 },
            );
          } else if (status === 401) {
            toast.error('Authentication failed. Please log in again.');
          } else {
            toast.error(
              getErrorMessage(
                error,
                'Failed to load merchants after multiple attempts.',
              ),
              { autoClose: 5000 },
            );
          }
        } else {
          toast.error(
            'No active merchants found for your account. Please contact support.',
            { autoClose: 5000 },
          );
        }
        setVerifyLoading(false);
        handleLogout();
        return;
      }

      dispatch(setMerchants(merchants));
      dispatch(setSelectedMerchant(merchants[0]));

      handleLogin();
      navigate('/', { state: { ...payload } });
    } catch (error: unknown) {
      setInputError(true);
      const msg = getErrorMessage(error);
      setError(msg);
      toast.error(msg, { autoClose: 5000 });
    } finally {
      setVerifyLoading(false);
    }
  };
  const handleCaptchaVerification = (value: string | null) => {
    if (value) {
      setRecaptchaVal(value);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="spacing.6">
      <Heading
        size="large"
        textAlign="center"
        color="surface.text.primary.normal"
      >
        Enter the 6 digit verification code
      </Heading>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="large"
        backgroundColor="surface.background.gray.intense"
        paddingX="spacing.8"
        paddingY="spacing.8"
        elevation="lowRaised"
      >
        <Box
          display="flex"
          flexDirection="column"
          gap="spacing.6"
          width={{ base: '100%', m: '506px' }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <OTPInput
              label="Verification Code"
              name="otp"
              otpLength={6}
              value={otp}
              onChange={({ value }) => {
                // Allow only digits and limit to 6 characters
                const sanitized = (value ?? '').replace(/\D/g, '').slice(0, 6);
                setOtp(sanitized);
                setInputError(false);
              }}
              onOTPFilled={({ value }) => {
                // OTP is complete, no additional action needed
                const sanitized = (value ?? '').replace(/\D/g, '').slice(0, 6);
                setOtp(sanitized);
              }}
              validationState={inputError ? 'error' : 'none'}
              errorText={error}
              isDisabled={verifyLoading || resendLoading}
              autoFocus
            />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="spacing.4"
              width="100%"
              marginTop="spacing.4"
            >
              <Button
                variant="primary"
                isDisabled={
                  !isOtpComplete ||
                  verifyLoading ||
                  resendLoading ||
                  (isProduction && !recaptchaVal)
                }
                isLoading={verifyLoading}
                onClick={handleFormSubmit}
                isFullWidth
              >
                Verify
              </Button>
              {isProduction && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap="spacing.4"
                >
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY || ''}
                    onChange={handleCaptchaVerification}
                  />
                  <Text size="small" color="surface.text.gray.muted">
                    Please complete the CAPTCHA verification to continue
                  </Text>
                </Box>
              )}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="spacing.2"
                width="100%"
              >
                {isResendDisabled && (
                  <Text size="small" color="surface.text.gray.muted">
                    Resend OTP in: {countdown}s
                  </Text>
                )}
                {!isResendDisabled && (
                  <Button
                    variant="secondary"
                    isLoading={resendLoading}
                    isDisabled={verifyLoading || resendLoading}
                    onClick={handleResendOtp}
                    isFullWidth
                  >
                    Resend OTP
                  </Button>
                )}
              </Box>
            </Box>
            <Box marginTop="spacing.8">
              <Link
                variant="button"
                onClick={onBackToLogin}
                isDisabled={verifyLoading || resendLoading}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OtpInputPage;
